// src/pages/PrivacyPolicyEN.jsx
import React from "react";

export default function PrivacyPolicyEN() {
  return (
    <div className="bg-white text-black min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-sm leading-relaxed md:text-base md:leading-relaxed">
        <header className="mb-10">
          <h1 className="text-2xl md:text-3xl font-serif mb-2">
            Privacy Policy
          </h1>
          <div className="text-gray-500 text-xs md:text-sm">
            Last updated: July 2026
          </div>
        </header>

        <section className="space-y-6 text-gray-800">
          <p>
            This Privacy Policy describes how <strong>Artkiv</strong> (operated
            by QUANG &amp; CO, 231 rue Saint-Honoré, 75001 Paris) collects,
            uses, and protects users’ personal data.
          </p>

          <p>
            Artkiv is an ArtTech platform designed to help collectors, experts,
            family offices, and insurers manage, value, and secure their works
            of art. Our services combine artificial intelligence, blockchain
            technology, and human expertise to ensure traceability, transmission,
            and the long-term preservation of art collections.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">1. Data collected</h2>
          <p>
            We collect only the data strictly necessary to provide Artkiv’s
            services.
          </p>

          <div className="space-y-3">
            <h3 className="font-medium">a. Identification data</h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>Name, first name, email address</li>
              <li>Encrypted password</li>
              <li>Phone number (optional)</li>
              <li>
                Billing and payment information (card details are processed by
                our secure payment provider — Artkiv does not store your card
                numbers).
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">b. Collection-related data</h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>
                Information about artworks: title, artist, medium, dimensions,
                image, estimate, provenance, certificates.
              </li>
              <li>Storage or exhibition location.</li>
              <li>
                History of transport, loans, sales, and insurance operations.
              </li>
              <li>Associated documents (invoices, certificates, reports).</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              c. Professional and partner data
            </h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>
                Contact details of experts, insurers, transporters, family
                offices, notaries, or other professionals invited or appointed
                through Artkiv.
              </li>
              <li>IBAN of experts or partners for payment purposes.</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">
              d. Sensitive or patrimonial data
            </h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>Estimated or insured value of an artwork</li>
              <li>Storage address</li>
              <li>Transmission or heirs-related information</li>
            </ul>
            <p className="text-gray-700">
              These data are subject to reinforced protection and are never
              shared with any third party without your explicit action.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">e. Technical data</h3>
            <ul className="list-disc ml-5 text-gray-700">
              <li>Connection data: IP address, device type, browser.</li>
              <li>
                Activity log (artwork access, sharing activity, downloads).
              </li>
              <li>Technical and analytical cookies (see Cookie Policy).</li>
            </ul>
          </div>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">2. Purpose of processing</h2>
          <p>We use your data to:</p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              Provide and maintain Artkiv services (collection management,
              reporting, certification, appraisal, invoicing).
            </li>
            <li>Ensure traceability and document security for artworks.</li>
            <li>
              Allow secure data sharing with professionals you explicitly invite
              (experts, insurers, transporters, notaries, etc.).
            </li>
            <li>
              Personalize your experience and provide technical assistance.
            </li>
            <li>
              Produce internal statistics and improve the overall service
              quality.
            </li>
            <li>
              Comply with legal obligations (billing, fraud prevention,
              accounting).
            </li>
          </ul>
        </section>

        {/* 🔒 NEW SECTION : Access & Anonymous Statistics */}
        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            2 bis. Access to artworks & Anonymous statistics
          </h2>

          <h3 className="font-medium">a. Access to artworks</h3>
          <p className="text-gray-700">
            By default, artwork records (images, values, provenance, documents)
            are visible only to the <strong>account owner</strong>. You may
            grant access to a professional (expert, insurer, transporter, family
            office, auction house, notary) via an explicit invitation from your
            dashboard. Each invitation specifies the scope (collection or
            artwork) and the access level (read or edit) and can be revoked at
            any time.
          </p>
          <p className="text-gray-700">
            Artkiv applies a principle of <strong>strict confidentiality</strong>{" "}
            and <strong>data compartmentalization</strong>: Artkiv teams{" "}
            <strong>do not have unrestricted or permanent access</strong> to
            the content of your artworks (images, documents, values, provenance).
           </p>

          <p className="text-gray-700">
            Exceptional access may be necessary (support, security, expertise,
            or technical intervention) only when it is{" "}
            <strong>strictly supervised</strong>, <strong>limited in scope</strong>,{" "}
            and <strong>triggered by an explicit authorization</strong> or an
            action from you (e.g., a support mandate).
          </p>

          <h3 className="font-medium">b. Exceptional access & traceability</h3>
          <p className="text-gray-700">
            Any exceptional access to artwork data is subject to{" "}
            <strong>logging</strong> to ensure transparency and security.
            This traceability may include the identity of the viewer, date and
            time, reason for access, and the type of action performed.
          </p>
          <p className="text-gray-700">
            This information may be made available to the collector upon request
            or through their personal dashboard.
          </p>

          <h3 className="font-medium">c. Automated processing & AI</h3>
          <p className="text-gray-700">
            Certain services (valuation, visual analysis, conservation insights,
            classification, document extraction) may rely on{" "}
            <strong>automated processing</strong> and <strong>artificial intelligence</strong>.
            These processes are conducted <strong>without systematic human review</strong>{" "}
            of the artworks and are strictly limited to the data required to
            perform the requested service.
          </p>
          <p className="text-gray-700">
            No legally or patrimonially binding decision is made solely through
            automated processing where human intervention is required by law.
          </p>

          <h3 className="font-medium">b. Anonymous statistics (opt-in)</h3>
          <p className="text-gray-700">
            To improve our services while preserving your privacy, Artkiv may
            generate anonymized global statistics based only on artworks for
            which you have activated the option{" "}
            <em>“Allow this artwork to be used anonymously in global
            statistics.”</em>
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              These statistics are <strong>aggregated and anonymized</strong> —
              no artwork or owner can be identified.
            </li>
            <li>
              Monetary values are <strong>rounded and grouped</strong>, and
              results are only published when{" "}
              <strong>at least three artworks</strong> fall within the same
              category (<em>k-anonymity rule</em>).
            </li>
            <li>
              Aggregations cover general categories such as{" "}
              <strong>medium</strong>, <strong>artist</strong> (without artwork
              lists), and <strong>period</strong> (e.g., Old Masters, Modern,
              Contemporary).
            </li>
            <li>
              You can <strong>enable or disable</strong> this option for each
              artwork at any time. Statistics update periodically.
            </li>
          </ul>
          <p className="text-gray-700">
            In no case do these statistics include titles, images, exact values,
            descriptions, or any identifiers of artworks or users.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">3. Legal basis (GDPR)</h2>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              <strong>Contract performance:</strong> we need certain data to
              provide the service (account creation, artwork management,
              payments).
            </li>
            <li>
              <strong>Legitimate interest:</strong> securing the platform,
              preventing misuse, improving usability.
            </li>
            <li>
              <strong>Explicit consent:</strong> any third-party sharing
              (insurer, transporter, auction house, expert), advanced AI
              analysis, or participation in <strong>anonymous statistics</strong>{" "}
              requires your prior and revocable consent.
            </li>
          </ul>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">4. Data retention</h2>
          <p className="text-gray-700">
            Your data are retained for the duration of your use of the service
            and up to three (3) years after your last activity. Certain
            information (invoices, contractual proof) may be stored longer if
            required by law.
          </p>
          <p className="text-gray-700">
            Data used to measure our campaigns — events from the
            “Collection &amp; legacy” journey, including your e-mail address when
            you choose to provide it at the end of the quiz — are retained for a
            maximum of thirteen (13) months, then automatically deleted. If you
            create an account, your e-mail address is additionally retained as
            part of that account, under the periods stated above.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">5. Traceability & blockchain</h2>
          <p className="text-gray-700">
            To ensure authenticity and provenance, some technical elements
            (digital certificate fingerprint, unique artwork ID, certification
            date) may be recorded on a blockchain.
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>No personal data are included.</li>
            <li>No storage address or insurance value is revealed.</li>
            <li>
              No identity of the collector or heirs can be derived from these
              records.
            </li>
            <li>Blockchain entries are permanent and tamper-proof.</li>
          </ul>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            6. Artificial intelligence (Vision.artkiv.com)
          </h2>
          <p className="text-gray-700">
            Using Vision.artkiv.com and other AI modules requires specific
            consent.
          </p>
          <p className="text-gray-700">
            These modules may analyze images, documents, or metadata to help
            identify characteristics or assist with description and valuation.
          </p>
          <p className="text-gray-700">
            Artkiv does not reuse your content to train or improve AI models for
            other clients without your explicit consent. Temporary technical
            copies created for analysis are deleted automatically.
          </p>
          <p className="text-gray-700">
            These analyses are performed by our sub-processor{" "}
            <strong>OpenAI</strong> (see “Sub-processors and international
            transfers”), <strong>only if you have enabled consent to AI
            analysis</strong>. Without this consent, no document is sent to any
            AI service: your files remain stored and are processed manually.
            Data sent to the OpenAI API is not used to train its models.
          </p>
          <p className="text-gray-700">
            <strong>What is sent.</strong> Your images and documents are stored
            in Europe (Paris). When you launch an AI analysis, the images or
            documents concerned are sent to our AI provider (OpenAI), whose
            processing may take place outside the European Union (notably in
            the United States), under the safeguards described in the
            “Sub-processors and international transfers” section. We send only
            what the requested analysis needs: the image or document and,
            depending on the analysis, the artwork’s description. For the style
            analysis of an image, the artwork title and the artist’s name are
            not sent. A reminder is shown before each analysis is launched. The AI
            provider may securely retain the data sent through its interface
            (API) for up to 30 days, to provide the service and detect abuse,
            then deletes it.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">7. Data sharing</h2>
          <p className="text-gray-700">
            We do not sell your data. They may be shared:
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              With technical providers (hosting, billing, secure messaging)
              strictly required to operate the service.
            </li>
            <li>
              With professionals you explicitly invite: independent expert,
              insurer, transporter, notary, wealth advisor, family office,
              auction house.
            </li>
            <li>
              With competent authorities when required by law or regulation.
            </li>
          </ul>
          <p className="text-gray-700">
            Each sharing action results from your explicit instruction (e.g.,
            “Invite this expert to view this artwork”).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">
            7 bis. Sub-processors and international transfers
          </h2>
          <p className="text-gray-700">
            To operate the service, Artkiv relies on technical sub-processors,
            each bound by a data processing agreement (DPA) and limited to the
            data strictly necessary for its task:
          </p>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              <strong>Hosting, database and secure document storage</strong> —
              servers located in the{" "}
              <strong>European Union (Paris and Helsinki)</strong>: database
              and document storage in Paris (Supabase), hosting of the websites
              and the application in Paris (Hostinger), analysis services in
              Helsinki (Hetzner).
            </li>
            <li>
              <strong>OpenAI</strong> — artificial-intelligence analysis of the
              images and documents you submit (classification, description,
              valuation assistance), <strong>only with your consent</strong>.
              Data sent via the API is not used to train its models. Hosted in
              the United States.
            </li>
            <li>
              <strong>Stripe</strong> — payment and billing processing (Artkiv
              does not store your card numbers).
            </li>
            <li>
              Provider for <strong>transactional emails</strong> (confirmations,
              notifications).
            </li>
          </ul>
          <p className="text-gray-700">
            Your documents and your database are hosted{" "}
            <strong>in the European Union (Paris and Helsinki)</strong>. Only
            specific
            processing — <strong>AI analysis</strong> (with your consent) and{" "}
            <strong>payment</strong> — involves providers located in the United
            States. Such transfers are governed by the appropriate safeguards
            required by the GDPR, in particular the European Commission’s{" "}
            <strong>Standard Contractual Clauses</strong> (and, where
            applicable, the <em>EU–US Data Privacy Framework</em>).
          </p>
          <p className="text-gray-700">
            The list of sub-processors may change. If we add a new sub-processor
            processing your data, we will update this policy.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">8. Security & storage</h2>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>Data hosted on secure European servers.</li>
            <li>Encryption in transit (HTTPS/TLS) and at rest.</li>
            <li>
              Backups and access logging to minimize loss or fraud risks.
            </li>
            <li>
              Internal access restricted to authorized personnel under NDA.
            </li>
          </ul>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">9. Cookies</h2>
          <p className="text-gray-700">
            We use technical cookies required for platform functionality
            (authentication, security, payments). With your consent, we may use
            anonymized analytics cookies to measure audience and improve the
            service. No advertising cookies are used.
          </p>
          <p className="text-gray-700">
            You can manage your cookie preferences anytime via the banner or the
            Cookies page.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">10. Your rights</h2>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>Right of access and rectification</li>
            <li>Right to erasure (“right to be forgotten”)</li>
            <li>Right to data portability (export of your records)</li>
            <li>
              Right to restriction or objection to certain processing
            </li>
            <li>
              Right to withdraw your consent at any time, including for AI use,
              data sharing, or participation in anonymous statistics.
            </li>
          </ul>
          <p className="text-gray-700">
            To exercise your rights:{" "}
            <strong>privacy@artkiv.com</strong>  
            or by mail: QUANG &amp; CO — 231 rue Saint-Honoré, 75001 Paris.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">11. Export & account deletion</h2>
          <ul className="list-disc ml-5 text-gray-700 space-y-2">
            <li>
              You may download your data (artworks, attached documents) in an
              interoperable format.
            </li>
            <li>
              You may delete your account and request deletion of all data not
              subject to legal retention.
            </li>
          </ul>
          <p className="text-gray-700">
            Deletion requests are processed within a reasonable time (maximum 30
            days, excluding legal constraints).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">12. Changes</h2>
          <p className="text-gray-700">
            This Privacy Policy may evolve to reflect regulatory, technical, or
            functional updates. In case of significant change, you will be
            notified via the platform or by email.
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">13. Data protection contact</h2>
          <p className="text-gray-700">
            Data Protection Officer: <strong>privacy@artkiv.com</strong>
          </p>
          <p className="text-gray-700">
            Supervisory authority: CNIL (Commission Nationale de l’Informatique
            et des Libertés).
          </p>
        </section>

        <hr className="my-10 border-gray-200" />

        <section className="space-y-4">
          <h2 className="text-xl font-serif">14. Governing language</h2>
          <p className="text-gray-700">
            The French version of the legal agreements and policies is deemed the
            only current and valid version of this document. Any translated version
            is provided solely for your convenience, to make the French version
            easier to read and understand. Translated versions are not legally
            binding and cannot replace the French versions. In the event of any
            disagreement or conflict, the French-language legal agreements and
            policies shall prevail.
          </p>
        </section>

        <footer className="mt-12 text-[11px] text-gray-400 leading-relaxed">
          Artkiv — Art collection management and valuation platform.  
          Art is not just collected, it is transmitted.
        </footer>
      </div>
    </div>
  );
}
